/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable @octopusdeploy/custom-portal-rules/no-restricted-imports */
import { Button, Callout, CustomMenu, Divider, Switch, Tooltip } from "@octopusdeploy/design-system-components";
import { ArrowDownToSquareIcon } from "@octopusdeploy/design-system-icons";
import type { ActivityElement, GitRefResource, TaskDetailsResource } from "@octopusdeploy/octopus-server-client";
import { ActivityLogEntryCategory, ActivityStatus } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import cn from "classnames";
import { concat, flatten, without } from "lodash";
import * as React from "react";
import { useMediaQuery } from "react-responsive";
import { v4 } from "uuid";
import ActionList from "~/components/ActionList";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { Section } from "~/components/Section/Section";
import type { UniqueActivityElement } from "~/components/TaskLogLines/TaskLogBlock";
import TaskLogBlock from "~/components/TaskLogLines/TaskLogBlock";
import TaskProgress from "../TaskProgress";
import { ExpandCollapseItemButton } from "./ExpandCollapseItemButton";
import styles from "./style.module.less";
interface TaskLogProps {
    details: TaskDetailsResource;
    activityElements: UniqueActivityElement[];
    verbose: boolean;
    tail: boolean;
    initialExpandedId?: string;
    manuallyExpandedIds?: string[];
    onFetchRange(element: ActivityElement, start: number, end: number): void;
    isFetchDisabled: boolean;
    setVerbose(value: boolean): void;
    processId?: string;
    stepsCorrelationIds?: {
        [key: string]: string;
    };
    gitRef?: GitRefResource;
    onExpandedIdsChanged?: (expandedIds: string[]) => void;
}
interface TaskDetailState {
    expandedIds: string[];
    expandMode: ExpandMode;
    showTimestamps: boolean;
    showExpandMenu: boolean;
    reloadCount?: number;
    expandButtonElement?: Element;
}
export enum ExpandMode {
    All = "All",
    Interesting = "Interesting",
    Errors = "Errors",
    None = "None",
    Custom = "Custom"
}
const timingsLocalStorageKey = "Octopus.TaskLog.ShowTimings";
//eslint-disable-next-line react/no-unsafe
export default class TaskLog extends React.Component<TaskLogProps, TaskDetailState> {
    reloadCount = 0;
    private expandMenuId = v4();
    constructor(props: TaskLogProps) {
        super(props);
        // noinspection TsLint
        this.state = {
            showExpandMenu: false,
            showTimestamps: localStorage.getItem(timingsLocalStorageKey) === "true",
            expandedIds: this.getInterestingIds(),
            expandMode: ExpandMode.Interesting,
        };
    }
    componentDidMount() {
        this.reloadCount++;
        if (this.props.initialExpandedId) {
            this.updateStateWithInitialExpandedId(this.props);
        }
        if (this.props.manuallyExpandedIds) {
            this.setState({ expandedIds: this.props.manuallyExpandedIds, expandMode: ExpandMode.Custom, reloadCount: this.reloadCount });
        }
    }
    UNSAFE_componentWillReceiveProps(nextProps: TaskLogProps) {
        this.reloadCount++;
    }
    updateStateWithInitialExpandedId(props: TaskLogProps) {
        if (props.initialExpandedId) {
            this.setState({
                expandedIds: this.getParents(props.initialExpandedId),
                expandMode: ExpandMode.Custom,
                reloadCount: this.reloadCount,
            });
        }
    }
    getParents(id: string) {
        const seperator = "/";
        const firstSection = id.indexOf(seperator);
        const ids = [id];
        if (firstSection === -1) {
            // No first section... weird..
            return ids;
        }
        let parentSection = id.lastIndexOf(seperator);
        while (parentSection !== -1) {
            const parentId = id.substring(0, parentSection);
            parentSection = parentId.lastIndexOf(seperator);
            ids.push(parentId);
        }
        return ids;
    }
    setExpanded = (id: string, expanded: boolean) => {
        this.setState((prevState) => {
            const expandedIds = expanded ? concat(prevState.expandedIds, id) : without(prevState.expandedIds, id);
            this.props.onExpandedIdsChanged?.(expandedIds);
            return { expandedIds, expandMode: ExpandMode.Custom, focusId: null, showTimestamps: prevState.showTimestamps, showExpandMenu: prevState.showExpandMenu };
        });
    };
    getElementIdsMatching(predicate: (element: UniqueActivityElement) => boolean) {
        return flatten(this.props.activityElements.map((e) => this.getElementsMatchingRecursive(e, predicate))).map((e) => e.uniqueId);
    }
    getElementsMatchingRecursive(element: UniqueActivityElement, predicate: (element: UniqueActivityElement) => boolean): UniqueActivityElement[] {
        const ids = flatten(element.Children.map((l) => this.getElementsMatchingRecursive(l as UniqueActivityElement, predicate)));
        if (ids.length > 0 || predicate(element)) {
            ids.push(element);
        }
        return ids;
    }
    expand(expandMode: ExpandMode) {
        this.setState({ expandedIds: this.getIdsToExpand(expandMode), expandMode });
    }
    getIdsToExpand(mode: ExpandMode) {
        switch (mode) {
            case ExpandMode.All:
                return this.getElementIdsMatching((e) => true);
            case ExpandMode.Interesting:
                return this.getInterestingIds();
            case ExpandMode.Errors:
                return this.getElementIdsMatching((e) => e.LogElements.filter((l) => l.Category === ActivityLogEntryCategory.Error || l.Category === ActivityLogEntryCategory.Fatal).length > 0);
            case ExpandMode.None:
                return [];
            default:
                return this.state.expandedIds;
        }
    }
    getInterestingIds() {
        return this.getElementIdsMatching((e) => e.Status === ActivityStatus.Pending ||
            e.Status === ActivityStatus.Running ||
            e.Status === ActivityStatus.Canceled ||
            e.LogElements.filter((l) => l.Category === ActivityLogEntryCategory.Error || l.Category === ActivityLogEntryCategory.Fatal).length > 0);
    }
    renderActivityLogs = (element: UniqueActivityElement) => {
        const focusId = this.state.reloadCount === this.reloadCount ? this.props.initialExpandedId : null;
        return (<TaskLogBlock key={element.uniqueId} element={element} taskState={this.props.details.Task.State} collapsible={true} expandedIds={this.state.expandedIds} focusId={focusId!} showRunTime={true} showTimestamps={this.state.showTimestamps} setExpanded={(id, expanded) => this.setExpanded(id, expanded)} onFetchRange={this.props.onFetchRange} isFetchDisabled={this.props.isFetchDisabled} processId={this.props.processId} stepsCorrelationIds={this.props.stepsCorrelationIds} gitRef={this.props.gitRef} parentDuration={0}/>);
    };
    render() {
        const details = this.props.details;
        const hasWritten = details.ActivityLogs.filter((l) => l.LogElements.length > 0 || l.Children.length > 0).length > 0;
        const toggleTimings = () => {
            const value = !this.state.showTimestamps;
            localStorage.setItem(timingsLocalStorageKey, value ? "true" : "");
            this.setState({
                showTimestamps: value,
            });
        };
        const toggleExpandMenu = (e: React.MouseEvent) => {
            e.preventDefault();
            this.setState({
                showExpandMenu: !this.state.showExpandMenu,
                expandButtonElement: e.currentTarget,
            });
        };
        const physicalLogSizeExceedsViewLimit = this.props.details.PhysicalLogSize >= 10 * 1024 * 1024;
        const expandOnClick = (expandMode: ExpandMode) => this.expand(expandMode);
        return (<div>
                <Section bodyClassName={styles.taskLogHeading}>
                    <div>
                        <TaskProgress details={details}/>
                    </div>
                    <div className={cn(styles.taskActions)}>
                        <ActionList actions={[
                <Switch value={this.props.verbose} onChange={(value) => this.props.setVerbose(value)} label="Verbose"/>,
                <Switch value={this.state.showTimestamps} onChange={() => toggleTimings()} label="Timestamps and Categories"/>,
                <Button importance={"quiet"} onClick={toggleExpandMenu} label="Expand/Collapse" aria-expanded={this.state.showExpandMenu ? "true" : "false"} aria-haspopup="menu" aria-controls={this.expandMenuId}/>,
                <TaskLogActionsDivider />,
                <ViewRawLogButton taskId={details.Task.Id} physicalLogSizeExceedsViewLimit={physicalLogSizeExceedsViewLimit}/>,
                <DownloadRawLogButton downloadUrl={details.Links["Raw"]}/>,
            ]} alignStart={true}/>

                        <CustomMenu isOpen={this.state.showExpandMenu} anchorElement={this.state.expandButtonElement || null} menuId={this.expandMenuId} accessibleName={`${this.state.showExpandMenu ? "Hide" : "Show"} expand menu`} onClose={() => this.setState({
                showExpandMenu: !this.state.showExpandMenu,
                expandButtonElement: undefined,
            })}>
                            <ExpandCollapseItemButton expandMode={ExpandMode.All} label="Expand all" selectedExpandMode={this.state.expandMode} expand={expandOnClick}/>
                            <ExpandCollapseItemButton expandMode={ExpandMode.Interesting} label="Expand interesting" selectedExpandMode={this.state.expandMode} expand={expandOnClick}/>
                            <ExpandCollapseItemButton expandMode={ExpandMode.Errors} label="Expand errors" selectedExpandMode={this.state.expandMode} expand={expandOnClick}/>
                            <ExpandCollapseItemButton expandMode={ExpandMode.None} label="Collapse all" selectedExpandMode={this.state.expandMode} expand={expandOnClick}/>
                        </CustomMenu>
                    </div>
                </Section>
                <Section>
                    {hasWritten ? (<div>{this.props.activityElements.map(this.renderActivityLogs)}</div>) : (<Callout type={"information"} title="No Logs">
                            The task has not written any information to the log yet.
                        </Callout>)}
                </Section>
            </div>);
    }
    static displayName = "TaskLog";
}
const ViewRawLogButton = ({ taskId, physicalLogSizeExceedsViewLimit }: {
    taskId: string;
    physicalLogSizeExceedsViewLimit: boolean;
}) => {
    const navigate = useSpaceAwareNavigation();
    return (<Tooltip content="This is a large log file and may cause your browser to freeze or crash. Please download the file instead." open={physicalLogSizeExceedsViewLimit ? undefined : false}>
            <Button importance="quiet" label="Raw Log" onClick={() => navigate.navigate(links.taskRawLogPage.generateUrl({ taskId }))} disabled={physicalLogSizeExceedsViewLimit}/>
        </Tooltip>);
};
const DownloadRawLogButton = ({ downloadUrl }: {
    downloadUrl: string;
}) => {
    return (<Button importance="quiet" icon={<ArrowDownToSquareIcon size={20}/>} label="Download" onClick={() => {
            window.open(downloadUrl);
        }}/>);
};
const TaskLogActionsDivider = () => {
    // When the screen width is 928px / 58rem, hide the divider
    // This value is also used in the Less CSS styles for <TaskLog /> to wrap the Raw Log and Download buttons onto the next line together
    const hideDividerBreakpoint = 928;
    const isDividerHidden = useMediaQuery({ query: `(max-width: ${hideDividerBreakpoint}px)` });
    if (isDividerHidden) {
        return null;
    }
    return <Divider orientation="vertical"/>;
};
